import React, { useState, useEffect } from "react";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Divider,
  Checkbox,
  IconButton,
  Tooltip,
  Modal,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useUserContext } from "../../context/UserContext";
import {
  getUsersByOrganization,
  getAvailableWorkspacesForCreativeRequest,
  createCreativeRequest,
  getSuggestion,
} from "../../client/api";
import { CreativeRequestType, Workspace } from "../../context/types"; // Import the CreativeRequestType enum
import useStyles from "./styles"; // Import custom styles
import { useNavigate } from "react-router-dom";

const CreativeRequest: React.FC = () => {
  const classes = useStyles();
  const { user } = useUserContext();
  const navigate = useNavigate();
  const [organizationMembers, setOrganizationMembers] = useState<any[]>([]);
  const [selectedReceiver, setSelectedReceiver] = useState<string>("");
  const [availableWorkspaces, setAvailableWorkspaces] = useState<any[]>([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>("");

  // Form fields
  const [creativeType, setCreativeType] = useState<"video" | "static">("video");
  const [headerType, setHeaderType] = useState<"WH" | "BH">("WH");
  const [type, setType] = useState<keyof typeof CreativeRequestType>("AU");
  const [brand, setBrand] = useState<string>("");
  const [brandDescription, setBrandDescription] = useState<string>("");
  const [adDescription, setAdDescription] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [hooks, setHooks] = useState<string[]>([""]);
  const [bodies, setBodies] = useState<string[]>([""]);
  const [ctas, setCtas] = useState<string[]>([""]);
  const [notes, setNotes] = useState<string>("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [useAI, setUseAI] = useState<boolean>(false); // Toggle for AI suggestions
  const [mood, setMood] = useState<string>(""); // Mood for hooks
  const [isRequestSuccess, setIsRequestSuccess] = useState<boolean>(false);
  const [receiverName, setReceiverName] = useState<string>("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);

  // Fetch organization members on component mount
  useEffect(() => {
    const fetchOrganizationMembers = async () => {
      if (user?.organization) {
        const members = await getUsersByOrganization(user?.organization?._id);
        setOrganizationMembers(members);
      }
    };
    fetchOrganizationMembers();
  }, [user]);

  // Fetch available workspaces when receiver is selected
  useEffect(() => {
    const fetchAvailableWorkspaces = async () => {
      if (user && selectedReceiver) {
        const workspaces = await getAvailableWorkspacesForCreativeRequest(
          user?._id,
          selectedReceiver
        );
        setAvailableWorkspaces(workspaces);
        const receiverWorkspace = workspaces.find(
          (workspace: any) =>
            workspace.owner === selectedReceiver ||
            workspace.owner?._id === selectedReceiver
        );

        if (receiverWorkspace) {
          setSelectedWorkspace(receiverWorkspace._id);
        } else if (workspaces.length > 0) {
          // Fallback to first available workspace if no owner match
          setSelectedWorkspace(workspaces[0]._id);
        }
      }
    };
    fetchAvailableWorkspaces();
  }, [selectedReceiver, user]);

  // Handle form submission
  const handleSubmit = async () => {
    const creativeRequestData = {
      creativeType,
      headerType,
      type,
      brand,
      brandDescription,
      adDescription,
      country,
      hooks,
      bodies,
      ctas,
      notes,
      priority,
      requester: user?._id,
      receiver: selectedReceiver,
      creativesWorkspace: selectedWorkspace,
    };

    try {
      const createdRequest = await createCreativeRequest(creativeRequestData);
      // Find the receiver's name
      const receiver = organizationMembers.find(
        (member) => member._id === selectedReceiver
      );
      setReceiverName(receiver?.name || "");
      setIsRequestSuccess(true);
      // Redirect or show success message
    } catch (error) {
      console.error("Error creating creative request:", error);
    }
  };

  const handleNewRequest = () => {
    // Reset the form and state
    navigate("/creative-request");
    setIsRequestSuccess(false);
    setSelectedReceiver("");
    setSelectedWorkspace("");
    setCreativeType("video");
    setHeaderType("WH");
    setType("AU");
    setBrand("");
    setBrandDescription("");
    setAdDescription("");
    setCountry("");
    setHooks([""]);
    setBodies([""]);
    setCtas([""]);
    setNotes("");
    setPriority("medium");
    setUseAI(false);
    setMood("");
  };

  const generateAISuggestion = async (
    type: "hook" | "body" | "cta",
    index?: number
  ) => {
    const maxTokens = 50;
    let prompt = `Create a ${type.toUpperCase()} for an ad script with these details:\n`;
    prompt += `Brand: ${brand}\n`;
    prompt += `Brand Description: ${brandDescription}\n`;
    prompt += `Ad Description: ${adDescription}\n`;

    // Add context about other parts of the ad that already exist
    if (type === "hook") {
      prompt += `This is just the hook - I'll ask for body and call to action suggestions later.\n`;
      if (mood) {
        prompt += `The mood should be: ${mood}\n`;
      }
      prompt += `Make it attention-grabbing and concise.\n`;
    } else if (type === "body") {
      if (hooks.length > 0 && hooks[0].trim() !== "") {
        prompt += `The hook will be: "${hooks[0]}"\n`;
        prompt += `Now create a body that expands on the hook naturally.\n`;
        prompt += `Don't repeat the hook verbatim - build on its message.\n`;
      } else {
        prompt += `This is the body of the ad - I'll ask for call to action later.\n`;
        prompt += `Make it informative and engaging.\n`;
      }
    } else if (type === "cta") {
      if (bodies.length > 0 && bodies[0].trim() !== "") {
        prompt += `The body includes: "${bodies[0]}"\n`;
        if (hooks.length > 0 && hooks[0].trim() !== "") {
          prompt += `The hook is: "${hooks[0]}"\n`;
        }
        prompt += `Now create a strong call to action that flows naturally from these.\n`;
        prompt += `Make it action-oriented but don't repeat the body verbatim.\n`;
      } else {
        prompt += `This is the call to action for the ad.\n`;
        prompt += `Make it clear, compelling and action-oriented.\n`;
      }
    }

    // For regenerating existing suggestions
    if (index !== undefined) {
      const currentText =
        type === "hook"
          ? hooks[index]
          : type === "body"
          ? bodies[index]
          : ctas[index];

      prompt += `Improve this existing ${type}:\n"${currentText}"\n`;
      prompt += `Make it more engaging while keeping the core message.\n`;
    }

    prompt += `Respond with only the ${type} text, nothing else.`;

    try {
      const response = await getSuggestion(prompt, maxTokens);
      return response.suggestion;
    } catch (error) {
      console.error("Error generating AI suggestion:", error);
      return "";
    }
  };

  const handleAddHook = async () => {
    const newHooks = [...hooks];
    if (useAI) {
      const suggestion = await generateAISuggestion("hook");
      newHooks.push(suggestion);
    } else {
      newHooks.push("");
    }
    setHooks(newHooks);
  };

  const handleAddBody = async () => {
    const newBodies = [...bodies];
    if (useAI) {
      const suggestion = await generateAISuggestion("body");
      newBodies.push(suggestion);
    } else {
      newBodies.push("");
    }
    setBodies(newBodies);
  };

  const handleAddCta = async () => {
    const newCtas = [...ctas];
    if (useAI) {
      const suggestion = await generateAISuggestion("cta");
      newCtas.push(suggestion);
    } else {
      newCtas.push("");
    }
    setCtas(newCtas);
  };

  if (isRequestSuccess) {
    return (
      <Box sx={{ padding: 3, textAlign: "center" }}>
        <Typography variant="h4" gutterBottom className={classes.titles}>
          Creative Request Sent
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Creative request sent to {receiverName}
        </Typography>
        <Button variant="contained" color="primary" onClick={handleNewRequest}>
          Send a New Request
        </Button>
      </Box>
    );
  }
  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h4" className={classes.titles}>
          Send Creative Request
        </Typography>
        <Tooltip title="What is a Creative Request?">
          <IconButton
            onClick={() => setIsInfoModalOpen(true)}
            size="small"
            sx={{ color: "text.secondary" }}
          >
            <InfoOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Creative Request Info Modal */}
      <Modal
        open={isInfoModalOpen}
        onClose={() => setIsInfoModalOpen(false)}
        aria-labelledby="creative-request-info-modal"
        aria-describedby="creative-request-info-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "80%",
            maxWidth: "600px",
            maxHeight: "80vh",
            overflow: "auto",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" id="creative-request-info-modal" gutterBottom>
            What is a Creative Request?
          </Typography>
          <Divider sx={{ marginBottom: 2 }} />
          <Typography variant="body1" id="creative-request-info-description">
            <strong>Overview</strong>
            <p style={{ marginTop: "8px", marginBottom: "16px" }}>
              Creative Requests is a collaboration feature that enables you to assign video creation
              tasks to team members. When you send a creative request, a project is automatically
              created for the receiver with all the information you provide, streamlining the workflow.
            </p>

            <strong>How It Works</strong>
            <ol style={{ marginTop: "8px", marginBottom: "16px" }}>
              <li>Select a team member to receive the request.</li>
              <li>Choose the workspace where the project will be created.</li>
              <li>Fill in the creative details: type, brand info, hooks, bodies, and CTAs.</li>
              <li>Submit the request.</li>
              <li>The receiver gets notified and a project is automatically created for them.</li>
              <li>The receiver creates the video based on your brief.</li>
            </ol>

            <strong>Key Features</strong>
            <ul>
              <li><strong>AI Suggestions:</strong> Enable "Use AI" to get AI-generated suggestions for hooks, bodies, and CTAs based on your brand and ad descriptions.</li>
              <li><strong>Priority Levels:</strong> Set priority (Low, Medium, High) to help receivers manage their workload.</li>
              <li><strong>Multiple Hooks/Bodies/CTAs:</strong> Add as many variations as needed for A/B testing.</li>
            </ul>

            <strong>Coming Soon</strong>
            <ul>
              <li>Email notifications for new requests</li>
              <li>Request status tracking</li>
              <li>Request management dashboard</li>
            </ul>
          </Typography>
          <Button
            variant="contained"
            onClick={() => setIsInfoModalOpen(false)}
            sx={{ marginTop: 2 }}
          >
            Close
          </Button>
        </Box>
      </Modal>

      <Grid container spacing={3}>
        {/* Receiver */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel className={classes.inputLabel} id="receiverLabel">
              Receiver
            </InputLabel>
            <Select
              value={selectedReceiver}
              labelId="receiverLabel"
              label="Receiver"
              onChange={(e) => setSelectedReceiver(e.target.value as string)}
            >
              {organizationMembers.map((member) => (
                <MenuItem key={member._id} value={member._id}>
                  {member.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Workspace */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel className={classes.inputLabel} id="workspaceLabel">
              Workspace
            </InputLabel>
            <Select
              value={selectedWorkspace}
              labelId="workspaceLabel"
              label="Workspace"
              onChange={(e) => setSelectedWorkspace(e.target.value as string)}
              disabled={!selectedReceiver}
            >
              {availableWorkspaces.map((workspace) => (
                <MenuItem key={workspace._id} value={workspace._id}>
                  {workspace.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} /> {/* Add margin for spacing */}
        </Grid>
        {/* Creative Type */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel className={classes.inputLabel} id="creativeTypeLabel">
              Creative Type
            </InputLabel>
            <Select
              labelId="creativeTypeLabel"
              label="Creative Type"
              value={creativeType}
              onChange={(e) =>
                setCreativeType(e.target.value as "video" | "static")
              }
            >
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="static">Static</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Header Type */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel className={classes.inputLabel} id="headerTypeLabel">
              Header Type
            </InputLabel>
            <Select
              labelId="headerTypeLabel"
              label="Header Type"
              value={headerType}
              onChange={(e: any) => setHeaderType(e.target.value)}
            >
              <MenuItem value="WH">WH</MenuItem>
              <MenuItem value="BH">BH</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Type */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel className={classes.inputLabel} id="typeLabel">
              Type
            </InputLabel>
            <Select
              labelId="typeLabel"
              label="Type"
              value={type}
              onChange={(e) =>
                setType(e.target.value as keyof typeof CreativeRequestType)
              }
            >
              {Object.entries(CreativeRequestType).map(([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Brand */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Brand"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            slotProps={{
              inputLabel: {
                classes: { root: classes.inputLabel },
                shrink: true,
              },
            }}
          />
        </Grid>

        {/* Brand Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Brand Description"
            multiline
            rows={3}
            value={brandDescription}
            onChange={(e) => setBrandDescription(e.target.value)}
            slotProps={{
              inputLabel: {
                classes: { root: classes.inputLabel },
                shrink: true,
              },
            }}
          />
        </Grid>

        {/* Ad Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Ad Description"
            multiline
            rows={3}
            value={adDescription}
            onChange={(e) => setAdDescription(e.target.value)}
            slotProps={{
              inputLabel: {
                classes: { root: classes.inputLabel },
                shrink: true,
              },
            }}
          />
        </Grid>

        {/* Country */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            slotProps={{
              inputLabel: {
                classes: { root: classes.inputLabel },
                shrink: true,
              },
            }}
          />
        </Grid>

        {/* Priority */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel
              className={classes.inputLabel}
              id="priorityLabel"
              shrink
            >
              Priority
            </InputLabel>
            <Select
              value={priority}
              labelId="priorityLabel"
              label="Priority"
              onChange={(e) =>
                setPriority(e.target.value as "low" | "medium" | "high")
              }
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} /> {/* Add margin for spacing */}
        </Grid>
        {/* Hooks */}
        <Grid item xs={12}>
          <Box className={classes.sectionHeader}>
            <Typography variant="h6" className={classes.titles}>
              Hooks
            </Typography>
            <Box className={classes.aiCheckboxContainer}>
              <Typography className={classes.aiCheckboxLabel}>
                Use AI
              </Typography>
              <Tooltip title="Use AI to generate script suggestions">
                <Checkbox
                  classes={{ root: classes.checkboxRoot }}
                  checked={useAI}
                  onChange={(e) => setUseAI(e.target.checked)}
                  color="primary"
                  size="small"
                />
              </Tooltip>
            </Box>
            {useAI && (
              <Tooltip title="Set the desired tone for AI-generated hooks. AI will use the current mood for new suggestions - change it anytime.">
                <TextField
                  label="Mood"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  size="medium"
                  placeholder="Suggest a mood for the AI"
                  sx={{
                    width: "250px",
                    marginLeft: 2,
                    "& .MuiInputBase-root": {
                      height: "40px",
                    },
                  }}
                  slotProps={{
                    inputLabel: {
                      classes: { root: classes.inputLabel },
                      shrink: true,
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          {hooks.map((hook, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <TextField
                fullWidth
                label={`Hook ${index + 1}`}
                value={hook}
                onChange={(e) => {
                  const newHooks = [...hooks];
                  newHooks[index] = e.target.value;
                  setHooks(newHooks);
                }}
                slotProps={{
                  inputLabel: {
                    classes: { root: classes.inputLabel },
                    shrink: true,
                  },
                }}
              />
              <Tooltip title="Delete hook">
                <IconButton
                  onClick={() => {
                    const newHooks = [...hooks];
                    newHooks.splice(index, 1);
                    setHooks(newHooks);
                  }}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {useAI && (
                <Tooltip title="Generate new AI suggestion for current hook">
                  <IconButton
                    onClick={async () => {
                      const suggestion = await generateAISuggestion(
                        "hook",
                        index
                      );
                      const newHooks = [...hooks];
                      newHooks[index] = suggestion;
                      setHooks(newHooks);
                    }}
                    size="small"
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Button
            onClick={handleAddHook}
            startIcon={<AddIcon />}
            size="small"
            sx={{ mt: 1 }}
          >
            Add Hook
          </Button>
        </Grid>

        {/* Bodies */}
        <Grid item xs={12}>
          <Box className={classes.sectionHeader}>
            <Typography variant="h6" className={classes.titles}>
              Bodies
            </Typography>
            <Box className={classes.aiCheckboxContainer}>
              <Typography className={classes.aiCheckboxLabel}>
                Use AI
              </Typography>
              <Checkbox
                classes={{ root: classes.checkboxRoot }}
                checked={useAI}
                onChange={(e) => setUseAI(e.target.checked)}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
          {bodies.map((body, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <TextField
                fullWidth
                label={`Body ${index + 1}`}
                value={body}
                onChange={(e) => {
                  const newBodies = [...bodies];
                  newBodies[index] = e.target.value;
                  setBodies(newBodies);
                }}
                slotProps={{
                  inputLabel: {
                    classes: { root: classes.inputLabel },
                    shrink: true,
                  },
                }}
              />
              <Tooltip title="Delete body">
                <IconButton
                  onClick={() => {
                    const newBodies = [...bodies];
                    newBodies.splice(index, 1);
                    setBodies(newBodies);
                  }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {useAI && (
                <Tooltip title="Generate new AI suggestion for current body">
                  <IconButton
                    onClick={async () => {
                      const suggestion = await generateAISuggestion(
                        "body",
                        index
                      );
                      const newBodies = [...bodies];
                      newBodies[index] = suggestion;
                      setBodies(newBodies);
                    }}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Button onClick={handleAddBody} size="small">
            <AddIcon sx={{ width: "12px" }} /> Add Body
          </Button>
        </Grid>

        {/* CTAs */}
        <Grid item xs={12}>
          <Box className={classes.sectionHeader}>
            <Typography variant="h6" className={classes.titles}>
              CTAs
            </Typography>
            <Box className={classes.aiCheckboxContainer}>
              <Typography className={classes.aiCheckboxLabel}>
                Use AI
              </Typography>
              <Checkbox
                classes={{ root: classes.checkboxRoot }}
                checked={useAI}
                onChange={(e) => setUseAI(e.target.checked)}
                color="primary"
                size="small"
              />
            </Box>
          </Box>
          {ctas.map((cta, index) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}
            >
              <TextField
                fullWidth
                label={`CTA ${index + 1}`}
                value={cta}
                onChange={(e) => {
                  const newCtas = [...ctas];
                  newCtas[index] = e.target.value;
                  setCtas(newCtas);
                }}
                slotProps={{
                  inputLabel: {
                    classes: { root: classes.inputLabel },
                    shrink: true,
                  },
                }}
              />
              <Tooltip title="Delete CTA">
                <IconButton
                  onClick={() => {
                    const newCtas = [...ctas];
                    newCtas.splice(index, 1);
                    setCtas(newCtas);
                  }}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
              {useAI && (
                <Tooltip title="Generate new AI suggestion for current CTA">
                  <IconButton
                    onClick={async () => {
                      const suggestion = await generateAISuggestion(
                        "cta",
                        index
                      );
                      const newCtas = [...ctas];
                      newCtas[index] = suggestion;
                      setCtas(newCtas);
                    }}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ))}
          <Button onClick={handleAddCta} size="small">
            <AddIcon sx={{ width: "12px" }} /> Add CTA
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} /> {/* Add margin for spacing */}
        </Grid>
        {/* Notes */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            slotProps={{
              inputLabel: {
                classes: { root: classes.inputLabel },
                shrink: true,
              },
            }}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CreativeRequest;
